import React, { useState } from 'react';
import { Plus, Search, Edit, Trash2, Building2, Mail, Phone, MapPin, Hash, FileText, Target, Grid, List } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { Organization } from '../types';
import { useTenant } from '../contexts/TenantContext';

const OrganizationsList: React.FC = () => {
  const { organizations, organizationTypes, tenders, applications, addOrganization, updateOrganization, deleteOrganization } = useData();
  const { currentCompany } = useTenant();
  const [showForm, setShowForm] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState<Organization | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const [formData, setFormData] = useState({
    name: '',
    organization_type_id: '',
    comuna: '',
    rut: '',
    contact: '',
    email: '',
    company_id: currentCompany?.id || ''
  });

  // Filter organizations by current company
  const companyOrganizations = organizations.filter(org => org.company_id === currentCompany?.id);

  // Función para obtener licitaciones activas de una organización
  const getActiveTendersCount = (organizationId: string) => {
    const today = new Date();
    return tenders.filter(tender => 
      tender.organization_id === organizationId && 
      tender.company_id === currentCompany?.id &&
      new Date(tender.closing_date) > today &&
      (tender.flujo === 'Disponible' || tender.flujo === 'En Revision')
    ).length;
  };

  // Función para verificar si estoy postulando a licitaciones de esta organización
  const hasActiveApplications = (organizationId: string) => {
    const organizationTenders = tenders.filter(tender => 
      tender.organization_id === organizationId && 
      tender.company_id === currentCompany?.id
    );
    
    const tenderIds = organizationTenders.map(tender => tender.id);
    
    return applications.some(application => 
      application.company_id === currentCompany?.id &&
      tenderIds.includes(application.tender_id)
    );
  };

  // Función para obtener el número de postulaciones activas
  const getActiveApplicationsCount = (organizationId: string) => {
    const organizationTenders = tenders.filter(tender => 
      tender.organization_id === organizationId && 
      tender.company_id === currentCompany?.id
    );
    
    const tenderIds = organizationTenders.map(tender => tender.id);
    
    return applications.filter(application => 
      application.company_id === currentCompany?.id &&
      tenderIds.includes(application.tender_id)
    ).length;
  };

  const filteredOrganizations = companyOrganizations
    .filter(org => {
      const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (org.contact && org.contact.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           (org.rut && org.rut.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesType = !typeFilter || org.organization_type_id === typeFilter;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      // Primero: organizaciones con postulaciones activas
      const aHasApplications = hasActiveApplications(a.id);
      const bHasApplications = hasActiveApplications(b.id);
      
      if (aHasApplications && !bHasApplications) return -1;
      if (!aHasApplications && bHasApplications) return 1;
      
      // Segundo: organizaciones con licitaciones activas
      const aHasTenders = getActiveTendersCount(a.id) > 0;
      const bHasTenders = getActiveTendersCount(b.id) > 0;
      
      if (aHasTenders && !bHasTenders) return -1;
      if (!aHasTenders && bHasTenders) return 1;
      
      // Tercero: orden alfabético por nombre
      return a.name.localeCompare(b.name);
    });

  // Debug: mostrar información de las organizaciones
  console.log('Organizaciones filtradas:', filteredOrganizations.length);
  console.log('Tenders disponibles:', tenders.length);
  console.log('Applications disponibles:', applications.length);
  filteredOrganizations.forEach(org => {
    console.log(`${org.name}: ${getActiveTendersCount(org.id)} licitaciones, ${hasActiveApplications(org.id) ? 'SÍ' : 'NO'} postulando`);
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingOrganization) {
      updateOrganization(editingOrganization.id, formData);
    } else {
      addOrganization(formData);
    }
    resetForm();
  };

  const handleEdit = (organization: Organization) => {
    setEditingOrganization(organization);
    setFormData({
      company_id: currentCompany?.id || '',
      name: organization.name,
      organization_type_id: organization.organization_type_id,
      comuna: organization.comuna || organization.region || '', // Compatibilidad con datos existentes
      rut: organization.rut || '',
      contact: organization.contact || '',
      email: organization.email || ''
    });
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta organización?')) {
      deleteOrganization(id);
    }
  };

  const resetForm = () => {
    setFormData({
      company_id: currentCompany?.id || '',
      name: '',
      organization_type_id: '',
      comuna: '',
      rut: '',
      contact: '',
      email: ''
    });
    setEditingOrganization(null);
    setShowForm(false);
  };

  const getOrganizationTypeName = (typeId: string) => {
    return organizationTypes.find(t => t.id === typeId)?.name || 'No especificado';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizaciones</h1>
          <p className="text-gray-600">Gestiona las organizaciones de {currentCompany?.name}</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Vista de grilla"
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Vista de lista"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nueva Organización
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingOrganization ? 'Editar Organización' : 'Nueva Organización'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nombre de la organización"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RUT Organización
                </label>
                <input
                  type="text"
                  value={formData.rut}
                  onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="12.345.678-9"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo Organización *
                </label>
                <select
                  value={formData.organization_type_id}
                  onChange={(e) => setFormData({ ...formData, organization_type_id: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleccionar tipo</option>
                  {organizationTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comuna *
                </label>
                <input
                  type="text"
                  value={formData.comuna}
                  onChange={(e) => setFormData({ ...formData, comuna: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Comuna donde opera"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contacto
                </label>
                <input
                  type="text"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nombre del contacto"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="email@organización.cl"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingOrganization ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar organizaciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los tipos</option>
              {organizationTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Organizations Display */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredOrganizations.length > 0 ? (
          filteredOrganizations.map((organization) => (
            <div key={organization.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg mr-3">
                      <Building2 className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {organization.name}
                      </h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {getOrganizationTypeName(organization.organization_type_id)}
                        </span>
                        {hasActiveApplications(organization.id) && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Target className="h-3 w-3 mr-1" />
                            Postulando
                          </span>
                        )}
                        {!hasActiveApplications(organization.id) && getActiveTendersCount(organization.id) > 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <FileText className="h-3 w-3 mr-1" />
                            Oportunidades
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(organization)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(organization.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-2 text-blue-600" />
                    <span>{organization.comuna || organization.region || 'No especificada'}</span>
                  </div>
                  {organization.rut && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Hash className="h-4 w-4 mr-2 text-purple-600" />
                      <span>{organization.rut}</span>
                    </div>
                  )}
                  {organization.contact && (
                  <div className="flex items-center text-sm text-gray-600">
                      <Phone className="h-4 w-4 mr-2 text-green-600" />
                    <span>{organization.contact}</span>
                  </div>
                  )}
                  {organization.email && (
                  <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 mr-2 text-orange-600" />
                    <span>{organization.email}</span>
                  </div>
                  )}
                  
                  {/* Información de licitaciones y postulaciones */}
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-gray-600">
                        <FileText className="h-4 w-4 mr-2 text-blue-600" />
                        <span>{getActiveTendersCount(organization.id)} licitaciones activas</span>
                      </div>
                      {hasActiveApplications(organization.id) && (
                        <div className="flex items-center text-sm text-green-600 font-medium">
                          <Target className="h-4 w-4 mr-1" />
                          <span>{getActiveApplicationsCount(organization.id)} postulaciones</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full">
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay organizaciones</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || typeFilter ? 'No se encontraron organizaciones con los filtros aplicados.' : 'Comienza agregando tu primera organización.'}
              </p>
              {!searchTerm && !typeFilter && (
                <button
                  onClick={() => setShowForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Nueva Organización
                </button>
              )}
            </div>
          </div>
        )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organización
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ubicación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contacto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Licitaciones
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrganizations.length > 0 ? (
                  filteredOrganizations.map((organization) => (
                    <tr key={organization.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="p-2 bg-blue-100 rounded-lg mr-3">
                            <Building2 className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {organization.name}
                            </div>
                            {organization.rut && (
                              <div className="text-sm text-gray-500">
                                {organization.rut}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {getOrganizationTypeName(organization.organization_type_id)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {organization.comuna || organization.region || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {organization.contact || '-'}
                        </div>
                        {organization.email && (
                          <div className="text-sm text-gray-500">
                            {organization.email}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="font-medium">{getActiveTendersCount(organization.id)}</span> activas
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {hasActiveApplications(organization.id) ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Target className="h-3 w-3 mr-1" />
                            Postulando
                          </span>
                        ) : getActiveTendersCount(organization.id) > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <FileText className="h-3 w-3 mr-1" />
                            Oportunidades
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Sin actividad
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(organization)}
                            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(organization.id)}
                            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No hay organizaciones</h3>
                      <p className="text-gray-500 mb-4">Comienza agregando tu primera organización</p>
                      <button
                        onClick={() => setShowForm(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Nueva Organización
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsList;